import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, Search, Download, Star, ArrowRight, Plus, ChevronRight, Cpu, Mail, Globe, Lock, BarChart3, Cloud, Users, Layers, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DashboardLayout } from "@/pages/DashboardPage";
import { useAuth, API } from "@/App";
import axios from "axios";

const CATEGORY_ICONS = {
  email_security: Mail,
  vulnerability: Shield,
  network: Globe,
  crypto_security: Lock,
  compliance: BarChart3,
  siem: Layers,
  identity: Users,
  cloud_security: Cloud,
};

const CATEGORY_LABELS = {
  email_security: "Email Security",
  vulnerability: "Vulnerability",
  network: "Network",
  crypto_security: "Crypto",
  compliance: "Compliance",
  siem: "SIEM",
  identity: "Identity",
  cloud_security: "Cloud",
};

const PRICING_COLORS = {
  free: { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/20", label: "FREE" },
  freemium: { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/20", label: "FREEMIUM" },
  paid: { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/20", label: "PAID" },
};

export default function MarketplacePage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [activePricing, setActivePricing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const headers = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        const params = new URLSearchParams();
        if (activeCategory) params.set("category", activeCategory);
        if (activePricing) params.set("pricing", activePricing);
        const resp = await axios.get(`${API}/marketplace/agents?${params.toString()}`, { headers, withCredentials: true });
        setAgents(resp.data.agents || []);
      } catch {
        setAgents([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, activeCategory, activePricing]);

  const filtered = agents.filter(
    (a) => a.name?.toLowerCase().includes(search.toLowerCase()) || a.description?.toLowerCase().includes(search.toLowerCase())
  );

  const categories = Object.keys(CATEGORY_LABELS);

  return (
    <DashboardLayout title="Agent Marketplace">
      <div className="space-y-6" data-testid="marketplace-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>Security Agent Marketplace</h2>
            <p className="text-xs text-zinc-500 mt-1">Deploy curated and community-built AI security agents</p>
          </div>
          <Button onClick={() => navigate("/marketplace/build")} className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-semibold text-sm" data-testid="publish-agent-btn">
            <Plus className="w-4 h-4 mr-1" /> Publish Agent
          </Button>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search agents..."
              className="bg-zinc-900 border-zinc-800 rounded-sm h-10 pl-10 text-zinc-50 placeholder:text-zinc-600"
              data-testid="marketplace-search"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setActivePricing(activePricing === "free" ? null : "free")}
              className={`text-xs px-3 py-2 rounded-sm font-semibold tracking-wide transition-colors ${activePricing === "free" ? "bg-emerald-600 text-white" : "bg-zinc-900 text-zinc-400 border border-zinc-800 hover:bg-zinc-800"}`}
              data-testid="filter-free"
            >Free</button>
            <button
              onClick={() => setActivePricing(activePricing === "paid" ? null : "paid")}
              className={`text-xs px-3 py-2 rounded-sm font-semibold tracking-wide transition-colors ${activePricing === "paid" ? "bg-amber-600 text-white" : "bg-zinc-900 text-zinc-400 border border-zinc-800 hover:bg-zinc-800"}`}
              data-testid="filter-paid"
            >Premium</button>
          </div>
        </div>

        {/* Categories */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setActiveCategory(null)}
            className={`text-xs px-3 py-1.5 rounded-sm font-semibold tracking-wide transition-colors ${!activeCategory ? "bg-blue-600 text-white" : "bg-zinc-900 text-zinc-400 border border-zinc-800 hover:bg-zinc-800"}`}
            data-testid="cat-all"
          >All</button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
              className={`text-xs px-3 py-1.5 rounded-sm font-semibold tracking-wide transition-colors ${activeCategory === cat ? "bg-blue-600 text-white" : "bg-zinc-900 text-zinc-400 border border-zinc-800 hover:bg-zinc-800"}`}
              data-testid={`cat-${cat}`}
            >{CATEGORY_LABELS[cat]}</button>
          ))}
        </div>

        {/* Agent Grid */}
        {loading ? (
          <div className="text-center py-12 text-zinc-500">Loading agents...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((agent, i) => {
              const CatIcon = CATEGORY_ICONS[agent.category] || Cpu;
              const pricing = PRICING_COLORS[agent.pricing_type] || PRICING_COLORS.free;
              return (
                <div key={agent.agent_id || i} className="trace-border rounded-sm bg-zinc-900 p-5 group hover:scale-[1.01] hover:border-zinc-700 transition-all duration-200 flex flex-col" data-testid={`agent-card-${i}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-sm bg-zinc-800 flex items-center justify-center group-hover:bg-blue-600/10 transition-colors">
                      <CatIcon className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                    </div>
                    <span className={`text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 rounded-sm ${pricing.bg} ${pricing.text} border ${pricing.border}`}>
                      {pricing.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold tracking-tight text-zinc-50 group-hover:text-blue-400 transition-colors" style={{ fontFamily: "var(--font-heading)" }}>{agent.name}</h3>
                    {agent.is_curated && <span className="text-[8px] px-1.5 py-0.5 bg-blue-600/10 text-blue-400 border border-blue-600/20 rounded-sm font-bold uppercase tracking-wider">Official</span>}
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed mb-3 flex-1 line-clamp-3">{agent.description}</p>
                  <span data-testid={`agent-runtime-status-${agent.agent_id}`} className={`text-xs mb-3 ${agent.runtime_config ? 'text-cyan-300' : 'text-amber-300'}`}>{agent.runtime_config ? 'Executable · free beta' : 'Concept listing · runtime unavailable'}</span>
                  <Button data-testid={`run-agent-${agent.agent_id}`} disabled={!agent.runtime_config} onClick={() => navigate(`/runtime?agent=${agent.agent_id}`)} className="mb-4 bg-cyan-700 hover:bg-cyan-600 text-white">{agent.runtime_config ? 'Run scan' : 'Not executable'}</Button>
                  {/* Capabilities */}
                  <div className="flex gap-1 flex-wrap mb-3">
                    {agent.capabilities?.slice(0, 3).map((cap) => (
                      <span key={cap} className="text-[9px] px-1.5 py-0.5 bg-zinc-800 text-zinc-500 rounded-sm">{cap}</span>
                    ))}
                    {agent.capabilities?.length > 3 && <span className="text-[9px] text-zinc-600">+{agent.capabilities.length - 3}</span>}
                  </div>
                  {/* Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
                    <div className="flex items-center gap-3 text-xs text-zinc-500">
                      <span className="flex items-center gap-1 text-[10px]">{agent.runtime_config ? 'Evidence-backed scans' : 'Preview concept'}</span>
                    </div>
                    {agent.price ? (
                      <span className="text-sm font-bold text-zinc-200" style={{ fontFamily: "var(--font-heading)" }}>${agent.price}<span className="text-xs text-zinc-500 font-normal">/mo</span></span>
                    ) : (
                      <span className="text-xs text-emerald-400 font-semibold">Free</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {filtered.length === 0 && !loading && (
          <div className="text-center py-12">
            <Cpu className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">No agents found. Try a different search or category.</p>
          </div>
        )}

        {/* Community CTA */}
        <div className="trace-border rounded-sm bg-zinc-900 p-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="community-cta">
          <div>
            <h3 className="text-base font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>Build & Publish Your Own Agent</h3>
            <p className="text-sm text-zinc-400 mt-1">Configure a bounded scan engine and custom policy terms. Publish a working tool to the community.</p>
          </div>
          <Button onClick={() => navigate("/marketplace/build")} className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-semibold shrink-0" data-testid="build-agent-btn">
            Start Building <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
